import { Injectable } from '@angular/core';

export type UserRegistrationType = 'ARTIST' | 'LABEL' | string;

export interface RegistrationState {
  userType: UserRegistrationType;
  isInProgress: boolean;
  startedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationStateService {
  private readonly REGISTRATION_STATE_KEY = 'mzic_registration_state';

  /**
   * Marks the registration as in progress for a specific user type
   * @param userType - The type of user registration (ARTIST, LABEL, etc.)
   */
  setRegistrationInProgress(userType: UserRegistrationType): void {
    const state: RegistrationState = {
      userType,
      isInProgress: true,
      startedAt: Date.now(),
    };
    localStorage.setItem(this.REGISTRATION_STATE_KEY, JSON.stringify(state));
  }

  /**
   * Marks the registration as completed and clears the state
   */
  setRegistrationComplete(): void {
    localStorage.removeItem(this.REGISTRATION_STATE_KEY);
  }

  /**
   * Checks if any registration is currently in progress
   * @returns true if a registration is in progress
   */
  isRegistrationInProgress(): boolean {
    const state = this.getRegistrationState();
    return state?.isInProgress ?? false;
  }

  /**
   * Checks if registration is in progress for a specific user type
   * @param userType - The type of user registration to check
   * @returns true if the specified user type registration is in progress
   */
  isRegistrationInProgressForUserType(userType: UserRegistrationType): boolean {
    const state = this.getRegistrationState();
    return state?.isInProgress === true && state?.userType === userType;
  }

  /**
   * Gets the current registration state
   * @returns The current registration state or null if none exists
   */
  getRegistrationState(): RegistrationState | null {
    try {
      const stateJson = localStorage.getItem(this.REGISTRATION_STATE_KEY);
      if (!stateJson) return null;

      const state: RegistrationState = JSON.parse(stateJson);
      return this.isValidState(state) ? state : null;
    } catch {
      // Clear invalid state and return null
      this.setRegistrationComplete();
      return null;
    }
  }

  /**
   * Gets the user type of the current registration in progress
   * @returns The user type or null if no registration is in progress
   */
  getCurrentRegistrationUserType(): UserRegistrationType | null {
    const state = this.getRegistrationState();
    return state?.isInProgress ? state.userType : null;
  }

  /**
   * Clears any existing registration state (useful for cleanup/logout)
   */
  clearRegistrationState(): void {
    this.setRegistrationComplete();
  }

  /**
   * Validates the registration state structure
   * @param state - The state to validate
   * @returns true if the state is valid
   */
  private isValidState(state: any): state is RegistrationState {
    return (
      typeof state === 'object' &&
      state !== null &&
      typeof state.userType === 'string' &&
      typeof state.isInProgress === 'boolean' &&
      typeof state.startedAt === 'number'
    );
  }
}
